import { useCurrentEditor } from '@tiptap/react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonV2 } from '../../button-v2/button-v2';
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from '../../popover/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip/tooltip';

export const ImageButton = () => {
  const { t } = useTranslation();
  const { editor } = useCurrentEditor();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // FileReader.onload may fire after the editor swaps or unmounts; read from the ref
  // at callback time rather than closing over a possibly-stale instance.
  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  });

  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState('');

  if (!editor) {
    return null;
  }

  const insertUrl = () => {
    if (!src) {
      return;
    }

    editor.chain().focus().setImage({ src }).run();
    setOpen(false);
    setSrc('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSrc('');
    }
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange again.
    event.target.value = '';
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const current = editorRef.current;
      if (!current || current.isDestroyed) {
        return;
      }

      const dataUrl = reader.result;
      if (typeof dataUrl === 'string') {
        current.chain().focus().setImage({ src: dataUrl }).run();
        setOpen(false);
        setSrc('');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={<PopoverTrigger />}
          className="tiptap-toolbar-button"
          aria-label={t('insert-image')}
          onMouseDown={(event) => event.preventDefault()}
        >
          <ImageIcon />
        </TooltipTrigger>
        <TooltipContent>{t('insert-image')}</TooltipContent>
      </Tooltip>
      <PopoverContent side="bottom" align="start" className="w-80 gap-3">
        <PopoverHeader>
          <PopoverTitle>{t('insert-image')}</PopoverTitle>
        </PopoverHeader>
        <label className="flex flex-col gap-1 text-foreground text-xs">
          {t('address')}
          <input
            type="url"
            value={src}
            onChange={(event) => setSrc(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                insertUrl();
              }
            }}
            placeholder="https://..."
            spellCheck={false}
            className="rounded-sm border border-input bg-background px-2 py-1 text-foreground text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
          />
        </label>
        <div className="flex items-center justify-between gap-2">
          <ButtonV2 variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload data-icon="inline-start" />
            {t('upload')}
          </ButtonV2>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
          <ButtonV2 disabled={!src} onClick={insertUrl}>
            {t('insert')}
          </ButtonV2>
        </div>
      </PopoverContent>
    </Popover>
  );
};
